import type { TeamOfficeGetResponse } from "@bezoned/shared/types/api/team/team-office-get.types.js";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import {
  getTeamOfficeAvailableChannels,
  officePresetsGet,
  teamLogoDelete,
  teamLogoUpdate,
  teamOfficeGet,
} from "../../../../../services/api.service.js";
import { recordErrorInSentry } from "../../../../../utils/init-sentry.js";
import type { TeamThemePreviewPreset } from "./components/team-edit-theme-preview-panel.js";

interface UseEditThemeStateParams {
  msTeamId: string;
  teamQueryData: TeamOfficeGetResponse | undefined;
  onChange: () => void;
  onError: (error: unknown) => void;
}

const buildCurrentPresetPreview = (
  isCurrentPreset: boolean,
  officeData: TeamOfficeGetResponse | undefined
): TeamThemePreviewPreset | undefined => {
  if (!(isCurrentPreset && officeData)) {
    return;
  }

  return {
    description: officeData.preset.description ?? "",
    manifest: {
      meetingRooms: officeData.preset.manifest.meetingRooms,
      tables: officeData.preset.manifest.tables,
    },
    name: officeData.preset.name,
    previewUrl: null,
  };
};

export const useEditThemeState = ({
  msTeamId,
  teamQueryData,
  onChange,
  onError,
}: UseEditThemeStateParams) => {
  const [logoChange, setLogoChange] = useState<File | null | undefined>();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [previewedPresetId, setPreviewedPresetId] = useState<string | null>(
    null
  );

  const queryClient = useQueryClient();
  const officeQueryKey = ["teamOfficeGet", msTeamId] as const;
  const channelsQueryKey = [
    "getTeamOfficeAvailableChannels",
    msTeamId,
  ] as const;

  const presetsQuery = useQuery({
    queryFn: () => officePresetsGet(),
    queryKey: ["officePresetsGet"],
  });

  const officeQuery = useQuery({
    queryFn: () => teamOfficeGet({ msTeamId }),
    queryKey: officeQueryKey,
  });

  const channelsQuery = useQuery({
    queryFn: () => getTeamOfficeAvailableChannels({ msTeamId }),
    queryKey: channelsQueryKey,
  });

  const editMutation = useMutation({
    mutationFn: ({ logoFile }: { logoFile?: File; logoUrl?: null }) =>
      logoFile
        ? teamLogoUpdate({ logoFile, msTeamId })
        : teamLogoDelete({ msTeamId }),
    onError: (error) => {
      recordErrorInSentry(error);
      onError(error);
    },
    onSuccess: () => {
      setLogoChange(undefined);
      setErrorMessage(null);
      onChange();
    },
  });

  const officePresets = presetsQuery.data?.presets ?? [];
  const officeData = officeQuery.data ?? teamQueryData;
  const currentPresetId = officeData?.preset.id ?? null;
  const currentLogoUrl = officeData?.logoUrl ?? null;

  const effectivePreviewedId = previewedPresetId ?? currentPresetId;
  const isCurrentPreset = effectivePreviewedId === currentPresetId;

  const previewedPreset = officePresets.find(
    (preset) => preset.id === effectivePreviewedId
  );

  const previewedPresetPreview: TeamThemePreviewPreset | undefined =
    previewedPreset ?? buildCurrentPresetPreview(isCurrentPreset, officeData);

  const previewedPresetName =
    previewedPreset?.name ??
    (isCurrentPreset
      ? (officeData?.preset.name ?? "Select a theme")
      : "Select a theme");

  const logoPreviewUrl = logoChange === null ? null : currentLogoUrl;

  const refreshOfficeData = () => {
    queryClient.invalidateQueries({ queryKey: officeQueryKey });
    queryClient.invalidateQueries({ queryKey: channelsQueryKey });
    onChange();
  };

  const handleSelectedFileChange = (file: File | null) => {
    if (!file) {
      setLogoChange((previousValue) =>
        previousValue instanceof File ? undefined : previousValue
      );
      return;
    }

    setLogoChange(file);
    editMutation.mutate({ logoFile: file });
  };

  const handleLogoDelete = () => {
    setLogoChange(null);
    editMutation.mutate({ logoUrl: null });
  };

  return {
    channelsQuery,
    editMutation,
    effectivePreviewedId,
    errorMessage,
    handleLogoDelete,
    handleSelectedFileChange,
    isCurrentPreset,
    logoPreviewUrl,
    officeData,
    officePresets,
    officeQuery,
    presetsQuery,
    previewedPresetId,
    previewedPresetName,
    previewedPresetPreview,
    refreshOfficeData,
    setErrorMessage,
    setPreviewedPresetId,
  };
};
